import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/latest'
import * as hooks from '@owl-nest/hooks'
import { fragment, query } from '@owl-nest/graphql'
import { Request, Response, responsiveImageFragment, ResponsiveImage } from '@owl-nest/contentful'

type Locale = api.Locale
export enum PartnerOperationStatus {
  ONGOING = 'En cours',
  ENDED = 'Terminé',
}

export type BasePartnersOperation = {
  cover: ResponsiveImage
  keyList: {
    json: any
  }
  partnerUserId: number | null
  status: PartnerOperationStatus
  tag: string
  urlMore: string
  urlProjectList: string
}

export type PartnersOperation = BasePartnersOperation & {
  description: {
    json: any
  }
}

export type PartnersOperationCollectionResult = {
  partnersOperationCollection: {
    items: PartnersOperation[]
  }
}

export type PartnersOperationInput = {
  locale?: api.Locale
  skip: number
  limit: number
}

export type OngoingPartnersOperation = BasePartnersOperation & {
  shortDescription: string | null
}

export type OngoingPartnersOperationCollectionResult = {
  partnersOperationCollection: {
    items: OngoingPartnersOperation[]
  }
}

export const BASE_PARTNERS_OPERATION_FRAGMENT = fragment`
    fragment BasePartnersOperation on PartnersOperation {
        cover {
          ...${responsiveImageFragment([320, 640, 960], 'PartnersOperationCover')}
        }
        keyList {
          json
        }
        partnerUserId
        status
        tag
        urlMore
        urlProjectList
    }
`
const PARTNERS_OPERATION_COLLECTION_QUERY = query<PartnersOperationCollectionResult, PartnersOperationInput>`
    query($locale: String, $skip: Int!, $limit: Int!) {
      partnersOperationCollection(
            order: [status_ASC, order_ASC]
            locale: $locale
            limit: $limit
            skip: $skip
        ) {
          items {
            ...${BASE_PARTNERS_OPERATION_FRAGMENT}
            description {
              json
            }

          }
        }
    }
`

const ONGOING_PARTNERS_OPERATION_COLLECTION_QUERY = query<
  OngoingPartnersOperationCollectionResult,
  PartnersOperationInput
>`
    query($locale: String, $skip: Int!, $limit: Int!) {
      partnersOperationCollection(
            order: [order_ASC]
            locale: $locale
            limit: $limit
            skip: $skip
            where: {
              status: "En cours"
            }
        ) {
          items {
            ...${BASE_PARTNERS_OPERATION_FRAGMENT}
            shortDescription
          }
        }
    }
`

export async function getPartnersOperationCollection(
  input: PartnersOperationInput,
  request: Request,
): Response<PartnersOperationCollectionResult> {
  const result = await request(PARTNERS_OPERATION_COLLECTION_QUERY, input)
  return result
}

export async function getOngoingPartnersOperationCollection(
  input: PartnersOperationInput,
  request: Request,
): Response<OngoingPartnersOperationCollectionResult> {
  const result = await request(ONGOING_PARTNERS_OPERATION_COLLECTION_QUERY, input)
  return result
}

export async function listForPartnersOperation(locale: Locale, request: Request) {
  const response = await getPartnersOperationCollection(
    {
      locale: locale ?? 'fr-FR',
      skip: 0,
      limit: 30,
    },
    request,
  )

  return response.next((response) => response)
}

export type PartnersOperationCollectionParams = {
  locale: Locale | 'fr-FR'
}

type PartnersOperationCollectionConfig = {
  fetchOnMount?: boolean | 'force'
  params: PartnersOperationCollectionParams
  request: Request
}

export const SHARED_PARTNERS_OPERATION_COLLECTION = jotai.atom<hooks.Cache<PartnersOperationCollectionResult>>({})

export function usePartnersOperationCollection({
  fetchOnMount = true,
  request,
  params,
}: PartnersOperationCollectionConfig) {
  const sharedQuery = hooks.useSharedQuery(
    async ({ locale }: PartnersOperationCollectionParams) => listForPartnersOperation(locale, request),
    {
      atom: SHARED_PARTNERS_OPERATION_COLLECTION,
      id,
    },
  )

  hooks.useFetchOnMount(
    {
      fetchOnMount,
      query,
      getResponse,
    },
    id(params),
  )

  return { response: getResponse() }

  function getResponse() {
    return sharedQuery.getCurrentResponse([params])
  }

  function query() {
    return sharedQuery.query(params)
  }
}

export function id({ locale }: PartnersOperationCollectionParams): string {
  return JSON.stringify([locale])
}
